import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import {
    fetchMemberSummary,
    triggerSummaryGeneration,
    stopProgressPolling,
    resetState,
} from '../../../store/memberAiSummary.slice';
import {
    Box,
    Paper,
    Typography,
    Stack,
    CircularProgress,
    IconButton,
    Tooltip,
    Link,
    Divider,
    Collapse,
    Alert,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { apiString as API_URL } from '../../../utils/constants';

interface MemberAiSummaryProps {
    memberId: string;
}

const MemberAiSummary: React.FC<MemberAiSummaryProps> = ({ memberId }) => {
    const dispatch = useAppDispatch();
    const { summary, progress, isLoading, isGenerating, error } = useSelector(
        (state: RootState) => state.memberAiSummary
    );

    const [isAiEnabled, setIsAiEnabled] = useState<boolean>(false);
    const [checkingStatus, setCheckingStatus] = useState<boolean>(true);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        const cached = localStorage.getItem(`ai_summary_collapsed_${memberId}`);
        return cached ? cached === 'true' : false;
    });

    // Re-read collapsed preference when memberId changes (component may be reused across members)
    useEffect(() => {
        const cached = localStorage.getItem(`ai_summary_collapsed_${memberId}`);
        setIsCollapsed(cached ? cached === 'true' : false);
    }, [memberId]);

    // Track if we've already triggered an auto-generation for this member.
    // Prevents the useEffect cascade from re-firing repeatedly when summary state changes.
    const autoTriggeredForMemberRef = useRef<string | null>(null);

    // Check AI dynamic enablement on mount
    useEffect(() => {
        const checkStatus = async () => {
            try {
                const res = await axios.get<boolean>(`${API_URL}/chpai/member-summary/enabled`);
                setIsAiEnabled(res.data);
            } catch (e) {
                console.error('Failed to check AI status:', e);
                setIsAiEnabled(false);
            } finally {
                setCheckingStatus(false);
            }
        };
        checkStatus();
    }, []);

    // Load summary and orchestrate auto-generation
    useEffect(() => {
        if (!memberId || !isAiEnabled) return;

        // Reset auto-trigger tracking when the member changes
        autoTriggeredForMemberRef.current = null;

        dispatch(fetchMemberSummary(memberId));

        return () => {
            dispatch(stopProgressPolling());
            dispatch(resetState());
        };
    }, [memberId, isAiEnabled, dispatch]);

    // Cascade generation after summary is fetched (one-shot per member)
    useEffect(() => {
        if (!memberId || !isAiEnabled || isLoading || isGenerating) return;

        // Bail out if we've already triggered an auto-generation for this member in this session
        if (autoTriggeredForMemberRef.current === memberId) return;

        // Auto trigger generation if summary doesn't exist, or if it's marked as stale by the backend
        if (summary === null || summary.isStale) {
            autoTriggeredForMemberRef.current = memberId;
            dispatch(triggerSummaryGeneration(memberId, false));
        } else {
            // Mark as handled even when no generation was necessary so we don't keep evaluating
            autoTriggeredForMemberRef.current = memberId;
        }
    }, [summary, memberId, isAiEnabled, isLoading, isGenerating, dispatch]);

    const handleRegenerate = () => {
        if (!memberId || isGenerating) return;
        // Allow a manual re-trigger to override the auto-trigger guard
        autoTriggeredForMemberRef.current = memberId;
        dispatch(triggerSummaryGeneration(memberId, true));
    };

    const toggleCollapse = () => {
        setIsCollapsed((prev) => {
            const next = !prev;
            localStorage.setItem(`ai_summary_collapsed_${memberId}`, String(next));
            return next;
        });
    };

    if (checkingStatus || !isAiEnabled) {
        return null;
    }

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
            });
        } catch {
            return dateString;
        }
    };

    return (
        <Paper
            elevation={3}
            sx={{
                mb: 3,
                borderRadius: 3,
                overflow: 'hidden',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 247, 250, 0.95))',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(0, 0, 0, 0.06)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
        >
            {/* Header Area */}
            <Box
                onClick={toggleCollapse}
                sx={{
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.015)',
                    },
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(45deg, #1976d2, #4791db)',
                            color: 'white',
                            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                        }}
                    >
                        <EmojiEventsRoundedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="700" color="text.primary">
                            AI Member Summary & Achievements
                        </Typography>
                        {summary && !isGenerating && (
                            <Typography variant="caption" color="text.secondary">
                                Last updated: {formatDate(summary.generatedAt)}
                            </Typography>
                        )}
                        {isGenerating && (
                            <Typography variant="caption" color="primary.main" fontWeight="500">
                                {progress?.stage ?? 'Generating...'}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1} onClick={(e) => e.stopPropagation()}>
                    {summary && !isGenerating && (
                        <Tooltip title="Force Re-generate Summary">
                            <IconButton onClick={handleRegenerate} size="small" sx={{ color: 'text.secondary' }}>
                                <RefreshRoundedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton onClick={toggleCollapse} size="small" sx={{ color: 'text.secondary' }}>
                        {isCollapsed ? <ExpandMoreRoundedIcon /> : <ExpandLessRoundedIcon />}
                    </IconButton>
                </Stack>
            </Box>

            <Collapse in={!isCollapsed}>
                <Divider />
                <Box sx={{ p: 3 }}>
                    {/* Error State */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Generating / Polling State */}
                    {isGenerating ? (
                        <Stack alignItems="center" justifyContent="center" spacing={2.5} py={6}>
                            <CircularProgress size={36} thickness={4.5} />
                            <Stack alignItems="center" spacing={0.5}>
                                <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                                    Summarizing Achievements & Goals
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {progress?.stage ?? 'Retrieving member care plan, goals, and history...'}
                                </Typography>
                            </Stack>
                        </Stack>
                    ) : isLoading ? (
                        <Stack alignItems="center" py={4}>
                            <CircularProgress size={28} />
                        </Stack>
                    ) : summary ? (
                        <Stack spacing={2}>
                            {summary.markdown ? (
                                <Typography variant="body1" component="div" sx={{ color: 'text.primary', lineHeight: 1.7, '& p': { m: 0 }, '& strong': { fontWeight: 600 } }}>
                                    <ReactMarkdown>{summary.markdown}</ReactMarkdown>
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No achievements or goals recorded yet.
                                </Typography>
                            )}

                            {/* Sources / Citations */}
                            {summary.citations && summary.citations.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <details>
                                        <summary style={{ fontSize: '0.8rem', color: '#666', fontWeight: '600', cursor: 'pointer', outline: 'none' }}>
                                            View supporting sources ({summary.citations.length})
                                        </summary>
                                        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75, pl: 1 }}>
                                            {summary.citations.map((c, ci) => (
                                                <Tooltip key={ci} title={c.excerpt ?? ''} placement="top" arrow>
                                                    <Link
                                                        href={c.url}
                                                        target="_blank"
                                                        rel="noopener"
                                                        underline="hover"
                                                        sx={{
                                                            fontSize: '0.725rem',
                                                            px: 1.25,
                                                            py: 0.5,
                                                            borderRadius: 1.5,
                                                            bgcolor: 'grey.100',
                                                            color: 'text.secondary',
                                                            border: '1px solid rgba(0,0,0,0.04)',
                                                            display: 'inline-block',
                                                            '&:hover': { bgcolor: 'grey.200' },
                                                        }}
                                                    >
                                                        {c.source}: {c.title}
                                                    </Link>
                                                </Tooltip>
                                            ))}
                                        </Box>
                                    </details>
                                </Box>
                            )}
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary" align="center" py={2}>
                            Summary is not available. Ensure dynamic AI is enabled.
                        </Typography>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default MemberAiSummary;
